import { useCallback, useMemo } from "react";
import { TrashIcon, FolderIcon } from "@heroicons/react/outline";

import { useRegistryDialogs } from "../Dialogs";
import { getItemActions } from "../utils";
import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import { Action, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { useRbac } from "@/providers/RbacProvider";

export const useContextualActions = (item: RegistryItem) => {
  const registry = useUnifiedRegistry();

  const { selectedEntities } = registry;
  const { clearSelection } = registry;
  const checkIsSelected = (registryItem: RegistryItem) => registry.isSelected(registryItem);
  const { selectedCount } = registry;

  const dialogs = useRegistryDialogs();
  const { isEditDisabled, isDeleteDisabled, isCreateDisabled } = useRbac();
  const hasSelection = selectedCount > 0;
  const isSelected = checkIsSelected(item);

  const handleBatchDelete = useCallback(async () => {
    if (!hasSelection) {
      return;
    }

    try {
      await dialogs.deleteBatch(selectedEntities, () => {
        registry.clearSelection();
      });
    } catch {
      // eslint-disable-next-line no-console
      console.error("Error handling batch delete");
    }
  }, [selectedEntities, dialogs, registry, hasSelection]);

  const handleBatchMove = useCallback(() => {
    if (!hasSelection) {
      return;
    }

    dialogs.openMoveFolder(selectedEntities);
  }, [selectedEntities, dialogs, hasSelection]);

  const batchActions = useMemo((): Action<RegistryItem>[] => {
    if (!hasSelection) {
      return [];
    }

    const bulkMoveDisabled = selectedEntities.some(entity => isEditDisabled(entity.type));
    const bulkDeleteDisabled = selectedEntities.some(entity => isDeleteDisabled(entity.type));
    const actions: Action<RegistryItem>[] = [];

    actions.push(
      {
        id: "batch-move",
        label: `Move to folder`,
        icon: FolderIcon,
        onClick: handleBatchMove,
        variant: DropdownMenuVariant.PRIMARY,
        getDisabled: () => bulkMoveDisabled,
        tooltipContent: bulkMoveDisabled
          ? "You don't have permission to move one or more selected items"
          : undefined,
      },
      {
        id: "batch-delete",
        label: `Delete ${selectedCount > 1 ? `all (${selectedCount})` : ""}`,
        icon: TrashIcon,
        onClick: handleBatchDelete,
        variant: DropdownMenuVariant.DESTRUCTIVE,
        getDisabled: () => bulkDeleteDisabled,
        tooltipContent: bulkDeleteDisabled
          ? "You don't have permission to delete one or more selected items"
          : undefined,
      },
    );

    return actions;
  }, [
    hasSelection,
    selectedCount,
    handleBatchMove,
    handleBatchDelete,
    isEditDisabled,
    isDeleteDisabled,
    selectedEntities,
  ]);

  const itemActions = useMemo(
    () =>
      getItemActions(
        item,
        { isEditDisabled, isDeleteDisabled, isCreateDisabled },
        {
          openMoveFolder: dialogs.openMoveFolder,
          openRenameEntity: dialogs.openRenameEntity,
          deleteEntity: dialogs.deleteEntity,
          openDuplicateEntity: dialogs.openDuplicateEntity,
          branchToWorkspace: dialogs.openBranchToWorkspace,
        },
      ),
    [item, dialogs, isEditDisabled, isDeleteDisabled, isCreateDisabled],
  );

  const contextMenuActions = useMemo(() => {
    if (isSelected && hasSelection && selectedCount > 1) {
      return batchActions;
    }
    return itemActions;
  }, [isSelected, hasSelection, batchActions, itemActions, selectedCount]);

  return {
    actions: contextMenuActions,
    contextMenuActions,
    hasSelection,
    selectedCount,
    isSelected,
    clearSelection,
  };
};
